import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';

/**
 * Base store factory for creating stores with advanced patterns
 * Provides caching, optimistic updates, error handling, and synchronization
 */
export function createStore(name, options = {}) {
  const {
    apiEndpoint,
    idField = 'id',
    cacheTimeout = 5 * 60 * 1000, // 5 minutes
    enableOptimistic = true,
    enableCache = true,
    enableSync = true,
    transformData = (data) => data,
    validateData = () => true,
  } = options;

  return defineStore(`improved-${name}`, () => {
    // Core State
    const items = ref(new Map());
    const loading = ref(false);
    const error = ref(null);
    const lastFetch = ref(null);
    const syncQueue = ref([]);
    const optimisticUpdates = ref(new Map());

    // Cache State
    const cacheTimestamps = ref(new Map());
    const pendingRequests = ref(new Map());

    // Sync State
    const syncInProgress = ref(false);
    const syncError = ref(null);
    const lastSync = ref(null);

    // Computed
    const itemsList = computed(() => {
      const result = [];
      for (const [id, item] of items.value) {
        // Apply optimistic updates if they exist
        const optimistic = optimisticUpdates.value.get(id);
        result.push(optimistic ? { ...item, ...optimistic } : item);
      }
      return result.sort((a, b) => {
        // Default sort by creation date or id
        const aDate = a.created_at || a[idField];
        const bDate = b.created_at || b[idField];
        return new Date(bDate) - new Date(aDate);
      });
    });

    const itemsById = computed(() => {
      const result = new Map();
      for (const [id, item] of items.value) {
        const optimistic = optimisticUpdates.value.get(id);
        result.set(id, optimistic ? { ...item, ...optimistic } : item);
      }
      return result;
    });

    const isLoading = computed(() => loading.value);
    const hasError = computed(() => !!error.value);
    const needsSync = computed(() => syncQueue.value.length > 0);
    const isCacheValid = computed(() => {
      if (!enableCache || !lastFetch.value) {return false;}
      return Date.now() - lastFetch.value < cacheTimeout;
    });

    // Actions
    const fetchAll = async (params = {}, force = false) => {
      // Check cache validity
      if (!force && isCacheValid.value && items.value.size > 0) {
        return itemsList.value;
      }

      // Check for pending request to avoid duplicates
      const requestKey = `fetchAll-${JSON.stringify(params)}`;
      if (pendingRequests.value.has(requestKey)) {
        return pendingRequests.value.get(requestKey);
      }

      loading.value = true;
      error.value = null;

      const request = async () => {
        try {
          const queryString = new URLSearchParams(params).toString();
          const url = `${apiEndpoint}${queryString ? `?${queryString}` : ''}`;
          
          const response = await fetch(url, {
            headers: await getAuthHeaders(),
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch ${name}: ${response.statusText}`);
          }

          const data = await response.json();
          
          let transformedData = [];
          if (Array.isArray(data)) {
            transformedData = data;
          } else if (data && data.data && Array.isArray(data.data.data)) {
            // Handle nested structure: {success: true, data: {data: [...], pagination: {...}}}
            transformedData = data.data.data;
          } else if (data && Array.isArray(data.data)) {
            transformedData = data.data;
          } else if (data && data.data) {
            transformedData = [];
          }
          
          
          // Validate and transform data
          const validatedData = transformedData
            .filter((item) => validateData(item))
            .map((item) => transformData(item));

          // Update store
          items.value.clear();
          validatedData.forEach((item) => {
            const id = item[idField];
            items.value.set(id, item);
            if (enableCache) {
              cacheTimestamps.value.set(id, Date.now());
            }
          });

          lastFetch.value = Date.now();
          return itemsList.value;
        } catch (err) {
          error.value = err.message;
          throw err;
        } finally {
          loading.value = false;
          pendingRequests.value.delete(requestKey);
        }
      };

      const promise = request();
      pendingRequests.value.set(requestKey, promise);
      return promise;
    };

    const fetchById = async (id, force = false) => {
      // Check cache
      if (!force && enableCache) {
        const cached = items.value.get(id);
        const cacheTime = cacheTimestamps.value.get(id);
        if (cached && cacheTime && Date.now() - cacheTime < cacheTimeout) {
          return cached;
        }
      }

      // Check for pending request
      const requestKey = `fetchById-${id}`;
      if (pendingRequests.value.has(requestKey)) {
        return pendingRequests.value.get(requestKey);
      }

      const request = async () => {
        try {
          const response = await fetch(`${apiEndpoint}/${id}`, {
            headers: await getAuthHeaders(),
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch ${name} ${id}: ${response.statusText}`);
          }

          const data = await response.json();
          const transformedData = transformData(data);

          if (!validateData(transformedData)) {
            throw new Error(`Invalid data received for ${name} ${id}`);
          }

          // Update store
          items.value.set(id, transformedData);
          if (enableCache) {
            cacheTimestamps.value.set(id, Date.now());
          }

          return transformedData;
        } catch (err) {
          error.value = err.message;
          throw err;
        } finally {
          pendingRequests.value.delete(requestKey);
        }
      };

      const promise = request();
      pendingRequests.value.set(requestKey, promise);
      return promise;
    };

    const create = async (data, optimistic = enableOptimistic) => {
      const tempId = optimistic ? `temp-${Date.now()}-${Math.random()}` : null;
      
      try {
        // Optimistic update
        if (optimistic && tempId) {
          const optimisticItem = {
            ...data,
            [idField]: tempId,
            _isOptimistic: true,
            created_at: new Date().toISOString(),
          };
          items.value.set(tempId, optimisticItem);
        }

        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...await getAuthHeaders(),
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error(`Failed to create ${name}: ${response.statusText}`);
        }

        const result = await response.json();
        const transformedResult = transformData(result);

        // Replace optimistic update with real data
        if (optimistic && tempId) {
          items.value.delete(tempId);
        }
        
        const realId = transformedResult[idField];
        items.value.set(realId, transformedResult);
        if (enableCache) {
          cacheTimestamps.value.set(realId, Date.now());
        }

        return transformedResult;
      } catch (err) {
        // Remove optimistic update on error
        if (optimistic && tempId) {
          items.value.delete(tempId);
        }
        
        // Add to sync queue if enabled
        if (enableSync) {
          syncQueue.value.push({
            action: 'create',
            data,
            timestamp: Date.now(),
            retries: 0,
          });
        }
        
        error.value = err.message;
        throw err;
      }
    };

    const update = async (id, data, optimistic = enableOptimistic) => {
      const originalItem = items.value.get(id);
      
      try {
        // Optimistic update
        if (optimistic && originalItem) {
          optimisticUpdates.value.set(id, data);
        }

        const response = await fetch(`${apiEndpoint}/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...await getAuthHeaders(),
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error(`Failed to update ${name} ${id}: ${response.statusText}`);
        }

        const result = await response.json();
        const transformedResult = transformData(result);

        // Apply real update
        optimisticUpdates.value.delete(id);
        items.value.set(id, transformedResult);
        if (enableCache) {
          cacheTimestamps.value.set(id, Date.now());
        }

        return transformedResult;
      } catch (err) {
        // Remove optimistic update on error
        if (optimistic) {
          optimisticUpdates.value.delete(id);
        }
        
        // Add to sync queue if enabled
        if (enableSync) {
          syncQueue.value.push({
            action: 'update',
            id,
            data,
            timestamp: Date.now(),
            retries: 0,
          });
        }
        
        error.value = err.message;
        throw err;
      }
    };

    const remove = async (id, optimistic = enableOptimistic) => {
      const originalItem = items.value.get(id);
      
      try {
        // Optimistic update
        if (optimistic && originalItem) {
          optimisticUpdates.value.set(id, { _isDeleted: true });
        }

        const response = await fetch(`${apiEndpoint}/${id}`, {
          method: 'DELETE',
          headers: await getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(`Failed to delete ${name} ${id}: ${response.statusText}`);
        }

        // Remove from store
        items.value.delete(id);
        optimisticUpdates.value.delete(id);
        cacheTimestamps.value.delete(id);

        return true;
      } catch (err) {
        // Remove optimistic update on error
        if (optimistic) {
          optimisticUpdates.value.delete(id);
        }
        
        // Add to sync queue if enabled
        if (enableSync) {
          syncQueue.value.push({
            action: 'delete',
            id,
            timestamp: Date.now(),
            retries: 0,
          });
        }
        
        error.value = err.message;
        throw err;
      }
    };

    const sync = async () => {
      if (!enableSync || syncQueue.value.length === 0) {return;}

      syncInProgress.value = true;
      syncError.value = null;

      try {
        const operations = [...syncQueue.value];
        const results = [];

        for (const operation of operations) {
          try {
            let result;
            switch (operation.action) {
              case 'create':
                result = await create(operation.data, false);
                break;
              case 'update':
                result = await update(operation.id, operation.data, false);
                break;
              case 'delete':
                result = await remove(operation.id, false);
                break;
            }
            results.push({ operation, result, success: true });
          } catch (err) {
            operation.retries = (operation.retries || 0) + 1;
            results.push({ operation, error: err, success: false });
          }
        }

        // Remove successful operations from queue
        syncQueue.value = syncQueue.value.filter((op) => {
          const result = results.find((r) => r.operation === op);
          return !result?.success && (result?.operation.retries || 0) < 3;
        });

        lastSync.value = Date.now();
        return results;
      } catch (err) {
        syncError.value = err.message;
        throw err;
      } finally {
        syncInProgress.value = false;
      }
    };

    const clearCache = () => {
      items.value.clear();
      cacheTimestamps.value.clear();
      optimisticUpdates.value.clear();
      lastFetch.value = null;
    };

    const clearErrors = () => {
      error.value = null;
      syncError.value = null;
    };

    const getById = (id) => {
      return itemsById.value.get(id) || null;
    };

    const filter = (predicate) => {
      const list = itemsList.value;
      return Array.isArray(list) ? list.filter(predicate) : [];
    };

    const search = (query, fields = ['name', 'title', 'description']) => {
      const list = itemsList.value;
      if (!query) {return Array.isArray(list) ? list : [];}

      const lowerQuery = query.toLowerCase();
      return Array.isArray(list) ? list.filter((item) => {
        return fields.some((field) => {
          const value = item[field];
          return value && String(value).toLowerCase().includes(lowerQuery);
        });
      }) : [];
    };

    // Auto-sync functionality
    let syncInterval = null;
    const startAutoSync = (intervalMs = 30000) => {
      if (syncInterval) {clearInterval(syncInterval);}
      syncInterval = setInterval(() => {
        if (needsSync.value && !syncInProgress.value) {
          sync();
        }
      }, intervalMs);
    };

    const stopAutoSync = () => {
      if (syncInterval) {
        clearInterval(syncInterval);
        syncInterval = null;
      }
    };

    // Utility functions
    const getAuthHeaders = async () => {
      // Use sessionStorage to match auth store token storage
      const token = sessionStorage.getItem('token');
      return token ? { 'Authorization': `Bearer ${token}` } : {};
    };

    // Watchers for automatic cleanup
    watch(() => items.value.size, (newSize) => {
      // Clean up old cache entries if we have too many items
      if (newSize > 1000) {
        const sortedEntries = Array.from(cacheTimestamps.value.entries())
          .sort(([,a], [,b]) => a - b);
        
        // Remove oldest 200 entries
        sortedEntries.slice(0, 200).forEach(([id]) => {
          items.value.delete(id);
          cacheTimestamps.value.delete(id);
          optimisticUpdates.value.delete(id);
        });
      }
    });

    return {
      // State
      items,
      loading,
      error,
      lastFetch,
      syncQueue,
      syncInProgress,
      syncError,
      lastSync,

      // Computed
      itemsList,
      itemsById,
      isLoading,
      hasError,
      needsSync,
      isCacheValid,

      // Actions
      fetchAll,
      fetchById,
      create,
      update,
      remove,
      sync,
      clearCache,
      clearErrors,
      getById,
      filter,
      search,
      startAutoSync,
      stopAutoSync,
    };
  });
}